import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { toneForStatus } from './statusTone';

import css from './PageTemplate.module.css';

/**
 * The one page template (P25009-431 BR-4): every data page is built from the
 * same five blocks in the same order —
 *
 *   <PageTemplate>
 *     <TemplateHeader title="Adresser" primaryAction={...} actions={...} />
 *     <FilterCard>…fields…</FilterCard>
 *     <TableCard resultCount={rows.length} actions={...}>
 *       <DataTable columns={[{ label: 'Namn' }, …]}>
 *         <tr><Td strong>…</Td><Td num>1 500 kr</Td><Td><StatusChip label="Aktiv" /></Td></tr>
 *       </DataTable>
 *     </TableCard>
 *   </PageTemplate>
 *
 * The result count lives in the table card's head ("N resultat"), money cells
 * are right-aligned in fixed-width digits (<Td num>), and status is always one
 * word in its own column (<StatusChip>). Blocks a page does not need are simply
 * omitted — the order of the ones present is what the template fixes.
 */

export const PageTemplate = ({ className, rootClassName, ground, children }) => (
  <div className={classNames(rootClassName || css.scope, { [css.ground]: ground }, className)}>
    {children}
  </div>
);

/**
 * BR-6: one primary button per page — `primaryAction` is a single slot, not a
 * list, so a second accent button has nowhere to go. Everything else goes in
 * `actions` and renders as secondary.
 */
export const TemplateHeader = ({ title, subtitle, primaryAction, actions }) => (
  <div className={css.pageHeader}>
    <div className={css.pageHeaderText}>
      <h1 className={css.pageTitle}>{title}</h1>
      {subtitle ? <p className={css.pageSubtitle}>{subtitle}</p> : null}
    </div>
    {actions || primaryAction ? (
      <div className={css.pageActions}>
        {actions}
        {primaryAction}
      </div>
    ) : null}
  </div>
);

/**
 * Secondary by default; `primary` paints the page's one accent button.
 * `as` renders another component (e.g. NamedLink) with the button's paint,
 * so a routing link can sit in the header without nesting an anchor in a
 * button.
 */
export const TemplateButton = ({ primary, className, as: As, children, ...rest }) => {
  const Tag = As || (rest.href ? 'a' : 'button');
  const ownType = Tag === 'button' ? { type: rest.type || 'button' } : {};
  return (
    <Tag
      className={classNames(primary ? css.btnPrimary : css.btn, className)}
      {...ownType}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export const FilterCard = ({ className, columns, children }) => (
  <div className={classNames(css.filterCard, className)}>
    <div
      className={css.filterGrid}
      style={columns ? { gridTemplateColumns: `repeat(${columns}, 1fr)` } : undefined}
    >
      {children}
    </div>
  </div>
);

/** One labelled field inside the filter card. */
export const FilterField = ({ label, children }) => (
  <div className={css.field}>
    {label ? <label>{label}</label> : null}
    {children}
  </div>
);

/**
 * BR-4: the card that carries the result count and the table. `resultCount`
 * renders "N resultat" (or `resultLabel` verbatim when a page needs different
 * wording); `actions` sits on the right of the same head row.
 */
export const TableCard = ({ resultCount, resultLabel, actions, className, children }) => {
  const hasHead = resultCount != null || resultLabel != null || actions != null;
  return (
    <div className={classNames(css.tableCard, className)}>
      {hasHead ? (
        <div className={css.tableCardHead}>
          <span className={css.resultCount}>
            {resultLabel != null ? (
              resultLabel
            ) : resultCount != null ? (
              <FormattedMessage id="PageTemplate.resultCount" values={{ count: resultCount }} />
            ) : null}
          </span>
          {actions ? <div className={css.tableCardHeadActions}>{actions}</div> : null}
        </div>
      ) : null}
      <div className={css.tableScroll}>{children}</div>
    </div>
  );
};

/**
 * The table block. `columns`: [{ label, align: 'right'? }]. Rows come as
 * children (<tr> of <Td>), so pages keep full control of their cells.
 */
export const DataTable = ({ columns = [], emptyMessage, children }) => {
  const rows = React.Children.count(children);
  return rows === 0 && emptyMessage ? (
    <div className={css.emptyRow}>{emptyMessage}</div>
  ) : (
    <table className={css.table}>
      <thead>
        <tr>
          {columns.map((col, i) => (
            <th key={i} className={col.align === 'right' ? css.thRight : null}>
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
};

/**
 * A table cell. `num` = money/amounts (right-aligned tabular figures),
 * `mono` = order numbers/dimensions/dates, `strong` = the row's title cell.
 */
export const Td = ({ num, mono, strong, className, children, ...rest }) => (
  <td
    className={classNames(
      { [css.tdNum]: num, [css.tdMono]: mono, [css.tdStrong]: strong },
      className
    )}
    {...rest}
  >
    {children}
  </td>
);

/**
 * BR-5: one word, four colour meanings. The tone is derived from the label via
 * the shared vocabulary; pass `tone` only to override a label the vocabulary
 * cannot know (e.g. a computed English key).
 */
export const StatusChip = ({ label, tone }) => {
  const resolved = tone || toneForStatus(label);
  const toneClass = {
    ok: css.chipOk,
    wait: css.chipWait,
    stop: css.chipStop,
    idle: css.chipIdle,
    info: css.chipInfo,
  }[resolved];
  return <span className={toneClass || css.chipIdle}>{label}</span>;
};

const { node, string, number, bool, arrayOf, shape, oneOf } = PropTypes;

PageTemplate.propTypes = { className: string, rootClassName: string, ground: bool, children: node };
TemplateHeader.propTypes = { title: node.isRequired, subtitle: node, primaryAction: node, actions: node };
TemplateButton.propTypes = { primary: bool, className: string, children: node };
FilterCard.propTypes = { className: string, columns: number, children: node };
FilterField.propTypes = { label: node, children: node };
TableCard.propTypes = {
  resultCount: number,
  resultLabel: node,
  actions: node,
  className: string,
  children: node,
};
DataTable.propTypes = {
  columns: arrayOf(shape({ label: node, align: oneOf(['right']) })),
  emptyMessage: node,
  children: node,
};
Td.propTypes = { num: bool, mono: bool, strong: bool, className: string, children: node };
StatusChip.propTypes = { label: node.isRequired, tone: oneOf(['ok', 'wait', 'stop', 'idle', 'info']) };

export { toneForStatus };
export default PageTemplate;
